import {
  type InvalidateQueryFilters,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { getQueryKey } from "@trpc/react-query"
import { useCallback, useMemo } from "react"

import { MAX_READBACK_SLICES } from "~/server/api/routers/demand2/shared/inputSchemas"
import { api, type RouterInputs, type RouterOutputs } from "~/utils/api"
import { refreshForecastDomainCache } from "~/utils/cacheUtils/forecastCacheHelpers"
import { invalidateLive } from "~/utils/updates/invalidateLive"
import { touchesVariant } from "~/utils/demandInvalidation"
import { CHANGES_AUTHORED_STATE, type DemandEditAction } from "~/utils/demandEditActions"

/**
 * Demand-2 cache refreshes, keyed by the domain event that caused them (`useInvalidateDemandReads`
 * after an edit, `useRefreshAfterPublish` after a publish); call sites never name a procedure.
 * A new read goes in `ENGINE_DERIVED_READS` or `PUBLISH_DERIVED_READS`, pinned by
 * `demandEngineReads.test.ts`.
 */

type DemandTotalsInput = RouterInputs["demand2"]["getDemandTotals"]
type DemandTotalsRow = RouterOutputs["demand2"]["getDemandTotals"][number]

/**
 * A cached totals entry's slice: its input minus `variantIds`. Derived, not hand-listed, so no
 * field is forgotten; sorted because call sites order the keys differently.
 */
const sliceKeyOf = (input: object): string =>
  JSON.stringify(
    Object.entries(input)
      .filter(([field]) => field !== "variantIds")
      .sort(),
  )

// `active`: some entry at the slice is mounted, so the page shows it.
type TotalsSlice = { input: DemandTotalsInput; keys: (readonly unknown[])[]; active: boolean }

/**
 * The cached totals entries touched by an edit, grouped by slice. Two entries in the same group answer the
 * same question of a different variant set, so one read of the touched variants at that slice patches both.
 */
function collectTotalsSlices(
  queryClient: QueryClient,
  touched: ReadonlySet<string>,
): { bySlice: Map<string, TotalsSlice>; unpatchable: (readonly unknown[])[] } {
  const matching = queryClient.getQueryCache().findAll({
    queryKey: getQueryKey(api.demand2.getDemandTotals),
    predicate: (q) => {
      const input = (q.queryKey[1] as { input?: DemandTotalsInput } | undefined)?.input
      const rows = q.state.data as DemandTotalsRow[] | undefined
      // A whole-collection entry names no variants on its key, so its rows say whether it holds one.
      if (input && !input.variantIds && rows) return rows.some((r) => touched.has(r.variantId))
      return touchesVariant(touched)(q)
    },
  })
  const bySlice = new Map<string, TotalsSlice>()
  const unpatchable: (readonly unknown[])[] = []
  for (const q of matching) {
    const input = (q.queryKey[1] as { input?: DemandTotalsInput } | undefined)?.input
    // Nothing to splice into: never resolved (keepPreviousData is returned to the observer, not
    // written to the cache), or a response already in flight that would land on top of the patch.
    if (!input || q.state.data === undefined || q.state.fetchStatus === "fetching") {
      unpatchable.push(q.queryKey)
      continue
    }
    const slice = sliceKeyOf(input)
    const entry = bySlice.get(slice)
    if (entry) {
      entry.keys.push(q.queryKey)
      entry.active ||= q.isActive()
    } else bySlice.set(slice, { input, keys: [q.queryKey], active: q.isActive() })
  }
  return { bySlice, unpatchable }
}

/** Splice one slice's fresh rows into every cached entry answering at that slice, matching by variantId. */
function spliceRows(
  queryClient: QueryClient,
  keys: (readonly unknown[])[],
  rows: DemandTotalsRow[],
): void {
  const byVariant = new Map(rows.map((r) => [r.variantId, r]))
  for (const queryKey of keys) {
    queryClient.setQueryData<DemandTotalsRow[]>(queryKey, (prev) =>
      prev?.map((row) => byVariant.get(row.variantId) ?? row),
    )
  }
}

// `next`: the queued read that variants touched now join; `last`: the read it starts after.
type SliceRead = { pending: Set<string>; next: Promise<void> | null; last: Promise<unknown> }

const sliceReads = new WeakMap<QueryClient, Map<string, SliceRead>>()

/**
 * One read in flight per slice; variants touched meanwhile wait and share the next one. A read costs
 * about the same for one variant as for many, so a stream of edits costs one read per read-time,
 * not one per edit. Reads of a slice run in order, so an older answer never lands over a newer one.
 * Resolves once the read carrying `variantIds` has landed.
 */
function readTotalsSlice(
  queryClient: QueryClient,
  utils: ReturnType<typeof api.useUtils>,
  slice: string,
  input: DemandTotalsInput,
  variantIds: ReadonlySet<string>,
): Promise<void> {
  const reads = sliceReads.get(queryClient) ?? new Map<string, SliceRead>()
  sliceReads.set(queryClient, reads)
  const state: SliceRead = reads.get(slice) ?? {
    pending: new Set(),
    next: null,
    last: Promise.resolve(),
  }
  reads.set(slice, state)
  for (const id of variantIds) state.pending.add(id)
  state.next ??= state.last.then(async () => {
    const ids = new Set(state.pending)
    state.pending.clear()
    state.next = null
    // Outside the query cache: a cached read of these variants still in flight predates the edit.
    const rows = await utils.client.demand2.getDemandTotals.query({
      ...input,
      variantIds: [...ids],
    })
    spliceRows(
      queryClient,
      collectTotalsSlices(queryClient, ids).bySlice.get(slice)?.keys ?? [],
      rows,
    )
  })
  state.last = state.next.catch(() => undefined)
  return state.next
}

/**
 * Fallback when the mutation carried no readback: re-read only the touched variants per slice on
 * screen and splice them in, rather than invalidating and re-running the engine over the whole
 * collection. Slices off screen are only invalidated: the cache keeps every scope visited.
 */
async function patchDemandTotals(
  queryClient: QueryClient,
  utils: ReturnType<typeof api.useUtils>,
  touched: ReadonlySet<string>,
): Promise<unknown> {
  const { bySlice, unpatchable } = collectTotalsSlices(queryClient, touched)
  const slices = [...bySlice.entries()]
  const offScreen = slices.filter(([, s]) => !s.active).flatMap(([, s]) => s.keys)
  return Promise.all([
    ...[...unpatchable, ...offScreen].map((queryKey) => invalidateLive(queryClient, { queryKey })),
    ...slices
      .filter(([, s]) => s.active)
      .map(([slice, { input }]) => readTotalsSlice(queryClient, utils, slice, input, touched)),
  ])
}

/** One slice an edit asks its mutation to read back: the cached input without the variant list. */
export type TotalsReadbackSlice = Omit<DemandTotalsInput, "variantIds">

/** One row array per requested slice, in the order the slices were sent. */
export type TotalsReadback = DemandTotalsRow[][]

/** Null when the mutation returned no totals, which sends the caller to the fallback re-read. */
export const readbackOf = (
  sent: TotalsReadbackSlice[] | undefined,
  result: { totals?: TotalsReadback | null } | null | undefined,
): TotalsReadbackRows | null => (sent && result?.totals ? { sent, rows: result.totals } : null)

/**
 * The slices on screen for `touched`, sent with an edit so the mutation returns their new numbers
 * (`demand2.*` has no batching link, so a re-read is a full round trip); `applyTotalsReadback`
 * invalidates the rest. Empty when no slice on screen is patchable; the edit then falls back to
 * `patchDemandTotals`.
 */
function readbackSlicesFor(queryClient: QueryClient, touched: string[]): TotalsReadbackSlice[] {
  if (touched.length === 0) return []
  const { bySlice } = collectTotalsSlices(queryClient, new Set(touched))
  return (
    [...bySlice.values()]
      .filter((s) => s.active)
      .slice(0, MAX_READBACK_SLICES)
      // The server substitutes the touched variants.
      .map(({ input: { variantIds: _drop, ...slice } }) => slice)
  )
}

export function useTotalsReadbackSlices(): (touched: string[]) => TotalsReadbackSlice[] {
  const queryClient = useQueryClient()
  return useCallback((touched: string[]) => readbackSlicesFor(queryClient, touched), [queryClient])
}

/**
 * Splice a mutation's returned rows into the cache, matching by slice rather than by position: the cache
 * can gain an entry while the mutation is in flight, and an entry no slice answers for is invalidated
 * rather than left holding pre-edit numbers.
 */
function applyTotalsReadback(
  queryClient: QueryClient,
  sent: TotalsReadbackSlice[],
  readback: TotalsReadback,
  touched: ReadonlySet<string>,
): Promise<unknown> {
  const rowsByKey = new Map<string, DemandTotalsRow[]>()
  sent.forEach((slice, i) => {
    const rows = readback[i]
    if (rows) rowsByKey.set(sliceKeyOf(slice), rows)
  })
  const { bySlice, unpatchable } = collectTotalsSlices(queryClient, touched)
  const stale = [...unpatchable]
  for (const [key, { keys }] of bySlice) {
    const rows = rowsByKey.get(key)
    if (rows) spliceRows(queryClient, keys, rows)
    else stale.push(...keys)
  }
  return Promise.all(stale.map((queryKey) => invalidateLive(queryClient, { queryKey })))
}

/**
 * Reads derived from a reconstruction pass, except `getDemandTotals` (patched per variant). Any
 * procedure calling a `shared/reconstructionRows` engine fetcher belongs here, or edits leave it
 * stale for the stale hour; `demandEngineReads.test.ts` trips on a new one.
 */
export const ENGINE_DERIVED_READS = [
  "getReconstructedDemand",
  "getDemandComposition",
  "getDemandTotalsByAxis",
  "getPreorderSplitPreview",
  "getPreorderSlicePreview",
  "getDiscountSliceFigures",
  "getAdjustmentTargetPreview",
  // Reads every covered variant's engine baseline.
  "getFramingSignals",
  // Runs the engine on the draft and under the active publish's kept edits.
  "explainSincePublish",
] as const

/**
 * Reads that depend on the active publish (`DemandPublish` / `DemandPublishedLeaf`), as
 * `[label, route]` so reads on other routers fit; tests assert on the label. `getDemandTotals` is
 * absent: it is patched per variant. Excluded on purpose: `getPublishedHistorySnapshot` (immutable)
 * and the ownership reads (`getPublishOverlap`, `getVariantOverlaps`), which no publish moves.
 */
export const PUBLISH_DERIVED_READS = [
  ["demand2.getPublishedVariantTotals", api.demand2.getPublishedVariantTotals],
  ["demand2.getDemandPublishStatus", api.demand2.getDemandPublishStatus],
  ["demand2.hasActivePublish", api.demand2.hasActivePublish],
  ["demand2.listDemandPublishStatus", api.demand2.listDemandPublishStatus],
  ["demand2.listDemandPublishes", api.demand2.listDemandPublishes],
  ["demand2.getPublishDiff", api.demand2.getPublishDiff],
  // The counterfactual side is the kept copy a publish replaces.
  ["demand2.explainSincePublish", api.demand2.explainSincePublish],
  ["demand2.listPublishedLeaves", api.demand2.listPublishedLeaves],
  // The portfolio's Demand figure: each variant's demand lock, else ForecastVariant.
  ["collection.getAllWithDetails", api.collection.getAllWithDetails],
  // `lockedAdjustments` checks ForecastVariantLocked type <> 'default'; a publish writes 'demand'.
  // `refreshForecastDomain` doesn't cover this read.
  ["npi.getNpiInfoSignals", api.npi.getNpiInfoSignals],
  // A market-split budget sources its forecast from the active publish's leaves (falling back to
  // ForecastVariant per variant), so publishing demand changes what both of these answer.
  ["budgets.getBudgetConstraintsForecasts", api.budgets.getBudgetConstraintsForecasts],
  ["budgets.getVariantBudgetBreakdown", api.budgets.getVariantBudgetBreakdown],
  // Planned side reads the active publish's leaves.
  ["budgets.getReconciliationPlan", api.budgets.getReconciliationPlan],
  ["budgets.getReconciliationRows", api.budgets.getReconciliationRows],
  ["budgets.getMarkdownImpact", api.budgets.getMarkdownImpact],
  // Stale "nothing published" skips the dialog's confirm, and the update then rejects.
  ["collection.getPublishedDateChangeImpact", api.collection.getPublishedDateChangeImpact],
  ["company.getPublishedDemandCoverage", api.company.getPublishedDemandCoverage],
  // Expected demand is the live published plan for the graded weeks, so a publish rewrites past weeks too.
  ["demand2.getPerformanceStats", api.demand2.getPerformanceStats],
  ["demand2.getPerformanceStatsByCell", api.demand2.getPerformanceStatsByCell],
  ["demand2.getPerformanceSeries", api.demand2.getPerformanceSeries],
  // End stock subtracts the active publish's leaves from stock on hand plus incoming.
  [
    "closingStock.getForecastedClosingStockForCollections",
    api.closingStock.getForecastedClosingStockForCollections,
  ],
  [
    "closingStock.getClosingStockBreakdownForCollections",
    api.closingStock.getClosingStockBreakdownForCollections,
  ],
] as const

/**
 * The three ledger readers, refreshed together: a stale cell popover attributes the current number to
 * the wrong edit, and a stale integrity count claims a hole that the edit just filled.
 */
const LEDGER_READS = [
  "listDemandEditEvents",
  "listDemandCellHistory",
  "getChangelogIntegrity",
] as const

/**
 * Reads whose `source: "draft"` input values the plan as it stands, so an edit moves them like
 * `getDemandTotals`; their published entries only move on publish (`PUBLISH_DERIVED_READS`).
 */
export const DRAFT_DERIVED_READS = [
  ["budgets.getReconciliationPlan", api.budgets.getReconciliationPlan],
  ["budgets.getReconciliationRows", api.budgets.getReconciliationRows],
  ["budgets.getMarkdownImpact", api.budgets.getMarkdownImpact],
] as const

const isDraftSourceQuery = (query: { queryKey: readonly unknown[] }) =>
  (query.queryKey[1] as { input?: { source?: string } } | undefined)?.input?.source === "draft"

/** The edit lists plus the stored size split, which no edit list carries. */
const EDIT_STATE_READS = ["getDemandEditState", "getSkuSplit"] as const

/**
 * What one edit refreshes. `{}` is the full set; a narrowing is for an edit that provably moves less,
 * declared in `READS_BY_ACTION` rather than as hand-rolled invalidations at the call site.
 */
export type DemandReadNarrowing = {
  /** Which engine reads move. Omit for all of `ENGINE_DERIVED_READS`, `[]` for none. */
  engine?: readonly (typeof ENGINE_DERIVED_READS)[number][]
  /** Whether the totals row set moves. Defaults to true. */
  totals?: boolean
  /** Whether the six edit lists and the stored size split move. Defaults to true. */
  editState?: boolean
  /** Whether the three ledger readers move. Defaults to true. */
  ledger?: boolean
  /** Reads outside the lists above that this edit moves. */
  also?: readonly (keyof typeof api.demand2 | keyof typeof OUTSIDE_DEMAND2_READS)[]
  /** Moves every variant of the plan, not only the edited ones, so nothing is patched per variant. */
  wholePlan?: boolean
}

const OUTSIDE_DEMAND2_READS = {
  "npiMatches.getManualMatches": api.npiMatches.getManualMatches,
} as const

const isOutsideDemand2Read = (name: string): name is keyof typeof OUTSIDE_DEMAND2_READS =>
  Object.hasOwn(OUTSIDE_DEMAND2_READS, name)

/** Reads of `DemandWorkspace.version`, which every authored edit bumps (`editedSincePublish`). */
const VERSION_READS = ["getDemandPublishStatus", "listDemandPublishStatus"] as const

// vs Published reads the draft split against the published one.
const SIZE_SPLIT_EDIT: DemandReadNarrowing = {
  engine: [],
  totals: false,
  also: ["getPublishedVariantTotals", "explainSincePublish"],
}

// A saved match moves the match counts and both baselines, which cache for an hour. As a fashion tier
// it also moves the worklist and Carry-over's Baseline, and brings a style with no comparable into
// pre-order development. The plan's framing answers (cannibalization) are sized on every variant's
// baseline, so a moved baseline moves the rest of the plan too. Matching the last new variant
// releases the held-back strategy answers.
const NPI_MATCH_EDIT: DemandReadNarrowing = {
  wholePlan: true,
  also: [
    "hasUnmatchedStrategyVariants",
    "getDemandFraming",
    "getCustomMatchesForCollection",
    "getNoosCustomMatchCounts",
    "getImpliedMatchBaseline",
    "getNpiBaselineDetail",
    "getNpiVariants",
    "getNewVariantSalesSignal",
    "getFashionComparables",
    "getFashionPreorderDevelopment",
    "npiMatches.getManualMatches",
  ],
}

// NOOS: a new number for the baseline source the variant already plans on. Its status, match
// counts and the held-back strategy answers stay. Only this tab narrows: colleagues refresh on the
// server's `match_none_set` / `baseline_source_set`.
const BASELINE_EDITS = {
  // Manual's pre-order share: moves only this variant's pre-order book.
  share: { editState: false, also: ["getNoosCustomMatchCounts"] },
  // Manual's units: move the whole plan as a match does, the curve "scaled by performance" reads,
  // and the accepted level the Match dialog seeds from.
  level: {
    editState: false,
    wholePlan: true,
    also: [
      "getNoosCustomMatchCounts",
      "getNewVariantSalesSignal",
      "getImpliedMatchBaseline",
      "getNpiBaselineDetail",
    ],
  },
  // "Matches, scaled by performance"'s index: moves the whole plan, and the offset `getNpiVariants` carries.
  index: { editState: false, wholePlan: true, also: ["getNpiVariants"] },
} as const satisfies Record<string, DemandReadNarrowing>

export type BaselineEdit = keyof typeof BASELINE_EDITS

/**
 * What each edit action moves, for the tab that made it and every colleague's alike: a mutation
 * names its actions (`useInvalidateDemandReads`), a live event carries them. Every action answers,
 * so a new one is a conscious choice. Authored actions (`CHANGES_AUTHORED_STATE`) move
 * `VERSION_READS` on top.
 */
const READS_BY_ACTION: Record<DemandEditAction, DemandReadNarrowing> = {
  override_set: {},
  override_clear: {},
  dated_override_set: {},
  share_set: {},
  adjustment_add: {},
  adjustment_edit: {},
  adjustment_delete: {},
  adjustment_reorder: {},
  insight_set: {},
  node_insight_set: {},
  insights_reviewed_set: {},
  variant_review_set: {},
  publish: {},
  size_split_set: SIZE_SPLIT_EDIT,
  size_split_clear: SIZE_SPLIT_EDIT,
  framing_set: { also: ["getDemandFraming"] },
  combinations_excluded_set: {
    editState: false,
    also: ["getDemandCombinations", "getDemandExcludedCombinations", "getPlannableCells"],
  },
  match_approve: NPI_MATCH_EDIT,
  match_remove: NPI_MATCH_EDIT,
  match_basis_set: NPI_MATCH_EDIT,
  match_none_set: NPI_MATCH_EDIT,
  match_none_clear: NPI_MATCH_EDIT,
  baseline_source_set: NPI_MATCH_EDIT,
  npi_level_set: NPI_MATCH_EDIT,
  npi_intro_weight_set: NPI_MATCH_EDIT,
}

const ALL_ACTIONS = Object.keys(READS_BY_ACTION) as DemandEditAction[]

/**
 * The procedure names a narrowing resolves to; exported for `demandInvalidation.test.ts`. Names
 * `getDemandTotals` even when the hook patches it: this answers what moves, not how.
 */
export function demandReadsFor(narrowing?: DemandReadNarrowing): string[] {
  const n = narrowing ?? {}
  return [
    ...(n.totals === false ? [] : ["getDemandTotals"]),
    ...(n.engine ?? ENGINE_DERIVED_READS),
    ...(n.editState === false ? [] : EDIT_STATE_READS),
    ...(n.ledger === false ? [] : LEDGER_READS),
    ...(n.also ?? []),
  ]
}

type DemandReads = { names: readonly string[]; engine: ReadonlySet<string>; wholePlan: boolean }

export type TotalsReadbackRows = { sent: TotalsReadbackSlice[]; rows: TotalsReadback }

const isDemandEditAction = (action: string): action is DemandEditAction =>
  Object.hasOwn(READS_BY_ACTION, action)

/** What edits with these actions move, together. None, or one this tab doesn't know, moves every action's. */
function readsForActions(actions: readonly string[] | undefined): DemandReads {
  const known = actions?.length && actions.every(isDemandEditAction) ? actions : ALL_ACTIONS
  return readsForNarrowings(
    known.map((action) => READS_BY_ACTION[action]),
    known.some((action) => CHANGES_AUTHORED_STATE[action]),
  )
}

function readsForNarrowings(
  narrowings: readonly DemandReadNarrowing[],
  authored: boolean,
): DemandReads {
  const engine = new Set(narrowings.flatMap((n) => n.engine ?? ENGINE_DERIVED_READS))
  const names = demandReadsFor({
    engine: [...engine],
    totals: narrowings.some((n) => n.totals !== false),
    editState: narrowings.some((n) => n.editState !== false),
    ledger: narrowings.some((n) => n.ledger !== false),
    also: [
      ...new Set([...narrowings.flatMap((n) => n.also ?? []), ...(authored ? VERSION_READS : [])]),
    ],
  })
  return { names, engine, wholePlan: narrowings.some((n) => n.wholePlan) }
}

/**
 * An edit reshapes only its own variants' reconstruction unless its action is `wholePlan`, so engine
 * reads refresh only where an edited variant is in their input; totals are patched per variant (`patchDemandTotals`); the cheap
 * edit lists are invalidated wholesale. Every refresh, this tab's or a colleague's, goes through
 * `invalidateLive`: a read in flight finishes and is followed up, never cancelled and restarted.
 */
function refreshDemandReads(
  utils: ReturnType<typeof api.useUtils>,
  queryClient: QueryClient,
  reads: DemandReads,
  variantIds: string[] | undefined,
  readback: TotalsReadbackRows | null | undefined,
): Promise<unknown> {
  const invalidate = (filters: InvalidateQueryFilters) => invalidateLive(queryClient, filters)
  const touched =
    !reads.wholePlan && variantIds && variantIds.length > 0 ? new Set(variantIds) : null
  const predicate = touched ? touchesVariant(touched) : undefined
  const draftReads = reads.names.includes("getDemandTotals")
    ? DRAFT_DERIVED_READS.map(([, route]) =>
        invalidate({ queryKey: getQueryKey(route), predicate: isDraftSourceQuery }),
      )
    : []
  return Promise.all([
    ...draftReads,
    ...reads.names.map((name) => {
      // No variant list = a collection-wide edit: every variant moves, so refetch wholesale.
      if (name === "getDemandTotals") {
        if (!touched) return invalidate({ queryKey: getQueryKey(api.demand2.getDemandTotals) })
        return readback
          ? applyTotalsReadback(queryClient, readback.sent, readback.rows, touched)
          : patchDemandTotals(queryClient, utils, touched)
      }
      if (isOutsideDemand2Read(name))
        return invalidate({ queryKey: getQueryKey(OUTSIDE_DEMAND2_READS[name]) })
      return invalidate({
        queryKey: getQueryKey(api.demand2[name as keyof typeof api.demand2]),
        predicate: reads.engine.has(name) ? predicate : undefined,
      })
    }),
  ])
}

/** Another planner's edit, from a live event: everything an edit with these actions moves. */
export function invalidateDemandReadsForEvent(
  utils: ReturnType<typeof api.useUtils>,
  queryClient: QueryClient,
  variantIds: string[] | undefined,
  actions: readonly string[] | undefined,
): Promise<unknown> {
  return refreshDemandReads(utils, queryClient, readsForActions(actions), variantIds, null)
}

/** Refreshes what the edits named by `actions` move (`READS_BY_ACTION`); none means any edit. */
export function useInvalidateDemandReads(...actions: DemandEditAction[]): (
  variantIds?: string[],
  /** Rows a mutation already read back, with the slices asked for. Absent ⇒ the fallback re-read. */
  readback?: TotalsReadbackRows | null,
) => Promise<unknown> {
  const utils = api.useUtils()
  const queryClient = useQueryClient()
  // Keyed on the joined actions, not the array (fresh each render), for a stable callback.
  const key = actions.join(",")
  const reads = useMemo(() => readsForActions(key === "" ? undefined : key.split(",")), [key])
  return useCallback(
    (variantIds?: string[], readback?: TotalsReadbackRows | null) =>
      refreshDemandReads(utils, queryClient, reads, variantIds, readback),
    [utils, queryClient, reads],
  )
}

const baselineEditReads = (edit: BaselineEdit): DemandReads =>
  readsForNarrowings([BASELINE_EDITS[edit]], true)

/** See `BASELINE_EDITS`. */
export function useRefreshBaselineEdit(): (
  variantId: string,
  edit: BaselineEdit,
) => Promise<unknown> {
  const utils = api.useUtils()
  const queryClient = useQueryClient()
  return useCallback(
    (variantId: string, edit: BaselineEdit) =>
      refreshDemandReads(utils, queryClient, baselineEditReads(edit), [variantId], null),
    [utils, queryClient],
  )
}

/**
 * Everything a publish moves; both publish paths call only this. Unfiltered per procedure: group
 * reads are keyed on a whole member list, so a member's publish must clear its group's entry too.
 * Engine reads refresh because `publishTail` (the plan past the active publish's last week) moves;
 * the forecast domain because publish writes `ForecastVariantLocked`.
 */
export function refreshAfterPublish(
  utils: ReturnType<typeof api.useUtils>,
  queryClient: QueryClient,
  variantIds: string[] | undefined,
): Promise<unknown> {
  return Promise.all([
    ...PUBLISH_DERIVED_READS.map(([, route]) =>
      invalidateLive(queryClient, { queryKey: getQueryKey(route) }),
    ),
    refreshDemandReads(utils, queryClient, readsForActions(["publish"]), variantIds, null),
    refreshForecastDomainCache(utils, variantIds),
  ])
}

export function useRefreshAfterPublish(): (variantIds: string[]) => Promise<unknown> {
  const utils = api.useUtils()
  const queryClient = useQueryClient()
  return useCallback(
    (variantIds: string[]) => refreshAfterPublish(utils, queryClient, variantIds),
    [utils, queryClient],
  )
}

/** Test-only. Call sites go through `useInvalidateDemandReads` / `useTotalsReadbackSlices`. */
export const __testing = {
  collectTotalsSlices,
  applyTotalsReadback,
  readsForActions,
  baselineEditReads,
  patchDemandTotals,
  readbackSlicesFor,
}
